/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { getDeliveryOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    FilterDeliveryModeOption,
    ProductDeliveryOptions,
    ReleasedProductType,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import {
    getSelectedProductIdFromActionInput,
    getSelectedVariant,
    SelectedVariantInput,
    QueryResultSettingsProxy,
    createGetFeatureStateInput,
    getFeatureState
} from '@msdyn365-commerce-modules/retail-actions';

/**
 * Input class for the GetDeliveryOptionsForSelectedProduct Data Action.
 */
export class GetDeliveryOptionsForSelectedProductInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public selectedProduct: SimpleProduct | undefined;

    public customerId: string;

    public featureState?: boolean;

    constructor(productId: number, channelId: number, selectedProduct?: SimpleProduct, customerId?: string, featureState?: boolean) {
        this.productId = productId;
        this.channelId = channelId;
        this.selectedProduct = selectedProduct;
        this.customerId = customerId || '';
        this.featureState = featureState;
    }

    public getCacheKey = () => 'DeliveryOptionsForSelectedProduct';

    public getCacheObjectType = () => 'DeliveryOptions';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * The createInput method for the GetDeliveryOptionsForSelectedProduct Data Action.
 * @param inputData The input data for the createInput method.
 */
export const createDeliveryOptionsForSelectedProductInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): GetDeliveryOptionsForSelectedProductInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new GetDeliveryOptionsForSelectedProductInput(
            +productId,
            +inputData.requestContext.apiSettings.channelId,
            undefined,
            undefined
        );
    }
    throw new Error('Unable to create PriceForSelectedProductInput, no productId found on module config or query');
};

/**
 * The Action Method for the GetDeliveryOptionsForSelectedProduct Data Action
 * Pulls the currently selected variant from the cache using the getSelectedProduct data action, and gets it's supported delivery options
 * via the getDeliveryOptionsAsync RetailServer API.
 * @param input
 * @param ctx
 */
export async function getDeliveryOptionsForSelectedProductAction(
    input: GetDeliveryOptionsForSelectedProductInput,
    ctx: IActionContext
): Promise<ProductDeliveryOptions | undefined> {
    if (input.featureState === undefined) {
        const featureState = await getFeatureState(createGetFeatureStateInput(ctx), ctx);
        input.featureState = featureState?.find(
            item => item.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature'
        )?.IsEnabled;
    }

    return Promise.resolve()
        .then(() => {
            const activeProduct: SimpleProduct | undefined = input.selectedProduct;

            if (!activeProduct) {
                const selectedVariantInput = new SelectedVariantInput(
                    input.productId,
                    input.channelId,
                    undefined,
                    undefined,
                    ctx.requestContext
                );

                return getSelectedVariant(selectedVariantInput, ctx);
            }

            return activeProduct;
        })
        .then((productResult: SimpleProduct | null) => {
            const activeProduct: SimpleProduct | undefined = <SimpleProduct | undefined>productResult;

            if (activeProduct) {
                return getDeliveryOptionsAsync(
                    { callerContext: ctx, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx) },
                    [activeProduct.RecordId],
                    {},

                    // For service item, should not apply the filter, otherwise there will be no electronic delivery mode
                    input.featureState && productResult?.ItemTypeValue !== ReleasedProductType.Service
                        ? FilterDeliveryModeOption.FilterOutNonPickupDeliveryMode
                        : null
                ).then(response => {
                    if (response && response.length > 0) {
                        return response[0];
                    }

                    return undefined;
                });
            }

            return undefined;
        })
        .catch((error: Error) => {
            ctx.trace(error.message);
            ctx.telemetry.exception(error);
            ctx.telemetry.debug('[getDeliveryOptionsForSelectedProductAction]Error executing action');
            throw new Error('[getDeliveryOptionsForSelectedProductAction]Error executing action');
        });
}

export const getDeliveryOptionsForSelectedProductActionDataAction = createObservableDataAction({
    id: 'retail-actions/get-delivery-options-for-selected-product',
    action: <IAction<ProductDeliveryOptions | undefined>>getDeliveryOptionsForSelectedProductAction,
    input: createDeliveryOptionsForSelectedProductInput
});

export default getDeliveryOptionsForSelectedProductActionDataAction;
